import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Plus, Loader2, Clock, CheckCircle, AlertCircle, Pause, Play, Square, AlertTriangle } from 'lucide-react';
import { listCampaigns, createCampaign, checkDuplicates } from '../api/campaigns';
import { listProfiles } from '../api/scrape';
import { useActiveProfile } from '../context/ActiveProfileContext';

const STATUS_BADGE = {
  generating: { color: 'bg-yellow-100 text-yellow-700', icon: Loader2, spin: true },
  draft: { color: 'bg-blue-100 text-blue-700', icon: Clock },
  active: { color: 'bg-green-100 text-green-700', icon: Play },
  paused: { color: 'bg-orange-100 text-orange-700', icon: Pause },
  stopped: { color: 'bg-slate-100 text-slate-600', icon: Square },
  completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

export default function Campaigns() {
  const navigate = useNavigate();
  const { activeProfileId } = useActiveProfile();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({ name: '', voiceProfileJobId: '', minScore: 70, maxProspects: 5 });
  const [error, setError] = useState('');
  const [dupCheck, setDupCheck] = useState(null); // { totalProspects, duplicateCount, newCount, duplicates }
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const { campaigns: list } = await listCampaigns();
      setCampaigns(list || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh when any campaign is generating
  useEffect(() => {
    const hasGenerating = campaigns.some(c => c.status === 'generating');
    if (!hasGenerating) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = async () => {
    setShowCreate(true);
    try {
      const res = await listProfiles();
      if (res.profiles) {
        setProfiles(res.profiles);
        // Auto-select the active voice profile if one is set
        if (activeProfileId && res.profiles.some(p => p.jobId === activeProfileId)) {
          setForm(f => ({ ...f, voiceProfileJobId: activeProfileId }));
        }
      }
    } catch {}
  };

  const handleCreate = async (skipDuplicates = false) => {
    if (!form.name.trim()) { setError('Campaign name is required'); return; }

    const body = {
      name: form.name,
      minScore: form.minScore || 70,
      maxProspects: form.maxProspects || 0,
    };
    if (form.voiceProfileJobId) body.voiceProfileJobId = form.voiceProfileJobId;

    // Step 1: Check for duplicates (only if we haven't already)
    if (!dupCheck && !skipDuplicates) {
      setChecking(true);
      setError('');
      try {
        const result = await checkDuplicates(body);
        if (result.duplicateCount > 0) {
          setDupCheck(result);
          setChecking(false);
          return; // Show duplicate UI, user decides
        }
        // No duplicates, proceed directly
      } catch {
        // If check fails, proceed anyway
      }
      setChecking(false);
    }

    // Step 2: Create campaign
    setCreating(true);
    setError('');
    try {
      // If user chose to skip duplicates, add excludeProspectIds
      if (skipDuplicates && dupCheck) {
        body.excludeProspectIds = dupCheck.duplicates.map(d => d.prospectId);
      }
      const res = await createCampaign(body);
      setShowCreate(false);
      setForm({ name: '', voiceProfileJobId: '', minScore: 70, maxProspects: 5 });
      setDupCheck(null);
      navigate(`/campaigns/${res.campaignId}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Campaigns</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your outbound email campaigns.</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Create Campaign</h2>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Q1 Cold Outreach"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Voice Profile (optional)</label>
            <select
              value={form.voiceProfileJobId}
              onChange={(e) => setForm({ ...form, voiceProfileJobId: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No voice profile</option>
              {profiles.map(p => (
                <option key={p.jobId} value={p.jobId}>
                  {p.companyName || p.url || p.jobId}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Min Priority Score</label>
            <input
              type="number"
              value={form.minScore}
              onChange={(e) => setForm({ ...form, minScore: parseInt(e.target.value) || 0 })}
              className="w-48 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">Only prospects with this score or higher will be included.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Max Prospects</label>
            <input
              type="number"
              value={form.maxProspects}
              onChange={(e) => setForm({ ...form, maxProspects: parseInt(e.target.value) || 0 })}
              className="w-48 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">Limit how many prospects to generate sequences for. Set 0 for no limit.</p>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Duplicate detection result */}
          {dupCheck && dupCheck.duplicateCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {dupCheck.duplicateCount} of {dupCheck.totalProspects} prospects already have sequences in other campaigns
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    {dupCheck.newCount} new prospects would be added. Choose how to handle the duplicates:
                  </p>
                </div>
              </div>
              <div className="flex gap-2 ml-8">
                <button
                  onClick={() => handleCreate(true)}
                  disabled={creating}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {creating && <Loader2 className="w-3 h-3 animate-spin" />}
                  Skip Duplicates ({dupCheck.newCount} new only)
                </button>
                <button
                  onClick={() => { setDupCheck(null); setCreating(true); setError('');
                    const b = { name: form.name, minScore: form.minScore || 70, maxProspects: form.maxProspects || 0 };
                    if (form.voiceProfileJobId) b.voiceProfileJobId = form.voiceProfileJobId;
                    createCampaign(b).then(res => {
                      setShowCreate(false); setForm({ name: '', voiceProfileJobId: '', minScore: 70, maxProspects: 5 });
                      navigate(`/campaigns/${res.campaignId}`);
                    }).catch(err => setError(err.message)).finally(() => setCreating(false));
                  }}
                  disabled={creating}
                  className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {creating && <Loader2 className="w-3 h-3 animate-spin" />}
                  Regenerate All ({dupCheck.totalProspects})
                </button>
                <button
                  onClick={() => setDupCheck(null)}
                  className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!dupCheck && (
            <div className="flex gap-3">
              <button
                onClick={() => handleCreate()}
                disabled={creating || checking}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {(creating || checking) && <Loader2 className="w-4 h-4 animate-spin" />}
                {checking ? 'Checking duplicates...' : 'Generate Sequences'}
              </button>
              <button
                onClick={() => { setShowCreate(false); setDupCheck(null); }}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Campaign list */}
      {campaigns.length === 0 && !showCreate ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Send className="w-7 h-7 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Campaigns Yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
            Create your first campaign to generate personalized email sequences for your prospects.
          </p>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const badge = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
            const Icon = badge.icon;
            return (
              <div
                key={c.campaignId}
                onClick={() => navigate(`/campaigns/${c.campaignId}`)}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{c.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Created {new Date(c.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-xs text-slate-500">
                      <div>{c.stats?.generated || 0} sequences</div>
                      <div>{c.stats?.sent || 0} sent</div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                      <Icon className={`w-3 h-3 ${badge.spin ? 'animate-spin' : ''}`} />
                      {c.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
